// src/components/Dashboard/Inventory/Maintenance_Cost.js
import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';

const MaintenanceCost = ({
  isLoading,
  error,
  stats,
  chartData,
  chartOptions,
  tableData,
  onRetry,
}) => {
  return (
    <div className="mt-10">
      <h2
        className={typography.h3}
        style={{ color: '#000000', marginBottom: '1rem' }}
      >
        Maintenance Cost
      </h2>
      <Card.Gradient className="p-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
            <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
            <p className={typography.p} style={{ color: '#333333' }}>
              Loading maintenance cost data...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
            <p className={typography.p} style={{ color: '#DC2626' }}>
              {error}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : !stats.length ? (
          <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
            <svg
              className="w-12 h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className={typography.p} style={{ color: '#666666' }}>
              No maintenance cost data for this period
            </p>
            <p
              className={typography.p}
              style={{ color: '#999999', fontSize: '0.875rem' }}
            >
              Adjust the filter to see available maintenance insights.
            </p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-[320px] flex-shrink-0 flex flex-col items-center gap-6">
              <div className="w-full max-w-[280px] h-[280px] relative">
                <Doughnut data={chartData} options={chartOptions} />
              </div>
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
                {stats.map((item, index) => (
                  <div key={`${item.category}-${index}`} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: chartData.datasets[0].backgroundColor[index] }}
                    />
                    <p
                      className={typography.p}
                      style={{ color: '#000000', fontSize: '0.875rem' }}
                    >
                      {item?.category || 'Unknown'} (
                      {Number(item?.percentage_of_total || 0).toFixed(1)}%)
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-x-auto overflow-y-auto max-h-[480px] rounded-lg border border-gray-300">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-100 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      type
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-200 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      Done Maintenance
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-100 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      Under Maintenance
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-200 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      Charges
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-100 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      Transportation
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-200 border-b border-neutral-200"
                      style={{ fontFamily: 'inherit' }}
                    >
                      Total charges
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr
                      key={row.type}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}
                    >
                      <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                        {row.type}
                      </td>
                      <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                        {row.doneMaintenance}
                      </td>
                      <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                        {row.underMaintenance}
                      </td>
                      <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                        ₹{row.charges.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                        ₹{row.transportation.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-black font-semibold border-b border-neutral-100">
                        ₹{row.totalCharges.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card.Gradient>
    </div>
  );
};

export default MaintenanceCost;

